import type { FC } from "react";
import { useEffect, useState } from "react";

import { usePushNotifications } from "../../push/usePushNotifications";

import StrawberryIcon from "images/icons/20-strawberry.svg";

const DISMISSED_KEY = "todas_push_dismissed";
/** Počet dní, po kterých se banner znovu zobrazí po zamítnutí */
const REDISPLAY_AFTER_DAYS = 1;

const wasRecentlyDismissed = () => {
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  const daysAgo = (Date.now() - parseInt(raw, 10)) / (1000 * 60 * 60 * 24);
  return daysAgo < REDISPLAY_AFTER_DAYS;
};

/**
 * Opt-in banner pro push notifikace.
 *
 * Zobrazí se jako fixed toast po přihlášení, pokud:
 * - Prohlížeč podporuje push (SW + PushManager + Notification API)
 * - Uživatel ještě nerozhodl (permissionState === "default")
 * - Nebyl v posledních 1 dni odmítnut
 */
export const PushNotificationOptIn: FC = () => {
  const { permissionState, requestPermission, declinePermission, isSupported } = usePushNotifications();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isSupported) return;
    if (permissionState === "loading") return;
    if (permissionState === "unavailable") {
      setVisible(true);
      return;
    }
    if (permissionState !== "default") return;
    if (wasRecentlyDismissed()) return;

    // Krátká prodleva, aby se banner nezobrazil okamžitě při načítání
    const timer = setTimeout(() => {
      setVisible(true);
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [permissionState, isSupported]);

  if (!visible) return null;

  if (permissionState === "unavailable") {
    return (
      <div className="push-opt-in" role="alert">
        <div className="push-opt-in__icon" aria-hidden="true">
          <StrawberryIcon width={32} height={32} />
        </div>
        <div className="push-opt-in__content">
          <p className="push-opt-in__title">Notifikace nejsou dostupné</p>
          <p className="push-opt-in__body">
            Tvůj prohlížeč blokuje push notifikace. Zkontroluj nastavení prohlížeče nebo zkus jiný.
          </p>
        </div>
        <div className="push-opt-in__actions">
          <button
            className="push-opt-in__btn push-opt-in__btn--secondary"
            onClick={() => {
              setVisible(false);
            }}
          >
            Zavřít
          </button>
        </div>
      </div>
    );
  }

  const handleAllow = () => {
    setVisible(false);
    void requestPermission();
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    void declinePermission();
  };

  return (
    <div className="push-opt-in" role="dialog" aria-label="Povolení notifikací">
      <div className="push-opt-in__icon" aria-hidden="true">
        <StrawberryIcon width={32} height={32} />
      </div>
      <div className="push-opt-in__content">
        <p className="push-opt-in__title">Nezapomeň trénovat!</p>
        <p className="push-opt-in__body">Povolíš notifikace, abychom ti mohli připomenout, že tě čeká procvičování?</p>
      </div>
      <div className="push-opt-in__actions">
        <button className="push-opt-in__btn push-opt-in__btn--primary" onClick={handleAllow}>
          Povolit
        </button>
        <button className="push-opt-in__btn push-opt-in__btn--secondary" onClick={handleDismiss}>
          Teď ne
        </button>
      </div>
    </div>
  );
};
